<template>
  <div id="app">
    <router-view />
  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import http from "@/lib/http";

const router = useRouter();

function clearSession() {
  try {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_handle");
    localStorage.removeItem("profile_details");
  } catch {}
}

onMounted(async () => {
  // ---- Security / UX guards ----
  // 1) Block context menu (right click / long-press menus)
  window.addEventListener("contextmenu", blockContextMenu);
  // 2) Block pinch-to-zoom on Safari (gesture* events)
  document.addEventListener("gesturestart", prevent, { passive: false });
  document.addEventListener("gesturechange", prevent, { passive: false });
  document.addEventListener("gestureend", prevent, { passive: false });
  // 3) Block Ctrl/Cmd zoom shortcuts and Ctrl+wheel zoom
  window.addEventListener("wheel", blockCtrlWheelZoom, { passive: false });
  window.addEventListener("keydown", blockZoomKeys, { passive: false });

  // auth freshness
  const token =
    localStorage.getItem("auth_token") || localStorage.getItem("access_token");
  if (!token) return;

  try {
    await http.get("/auth/me"); // or '/profile/me'
  } catch {
    clearSession();
    if (router.currentRoute.value.matched.some(r => r.meta?.requiresAuth)) {
      router.replace({
        name: "login",
        query: { redirect: router.currentRoute.value.fullPath || "/" },
      });
    }
  }
});

onBeforeUnmount(() => {
  // Cleanup listeners to avoid leaks in HMR / SPA nav
  window.removeEventListener("contextmenu", blockContextMenu);
  document.removeEventListener("gesturestart", prevent);
  document.removeEventListener("gesturechange", prevent);
  document.removeEventListener("gestureend", prevent);
  window.removeEventListener("wheel", blockCtrlWheelZoom);
  window.removeEventListener("keydown", blockZoomKeys);
});

// ----- handlers -----
function prevent(e) {
  e.preventDefault();
}

function blockContextMenu(e) {
  // Allow text inputs to keep their native context menu if you want:
  const tag = (e.target?.tagName || "").toLowerCase();
  const editable = e.target?.isContentEditable;
  if (editable || tag === "input" || tag === "textarea") return;
  e.preventDefault();
}

function blockCtrlWheelZoom(e) {
  // Prevent zoom when holding Ctrl/Cmd and scrolling
  if (e.ctrlKey || e.metaKey) e.preventDefault();
}

function blockZoomKeys(e) {
  // Prevent Ctrl/Cmd + (+/-/0/=) zoom shortcuts
  const zoomKeys = ["+", "-", "=", "0"];
  if ((e.ctrlKey || e.metaKey) && zoomKeys.includes(e.key)) {
    e.preventDefault();
  }
}
</script>

<style>
/* ---------- Global scroll + gesture behavior ---------- */

/* Keep normal scroll; hide scrollbars by default (mobile & small screens) */

/* WebKit (Chrome, Safari, Edge Chromium) */
::-webkit-scrollbar {
  display: none; /* hidden by default */
}

/* Firefox */
html {
  scrollbar-width: none; /* hides scrollbar but keeps scroll */
}

/* Old Edge/IE */
html {
  -ms-overflow-style: none;
}

/* Improve touch behavior without enabling zoom gestures */
html, body {
  /* Allow panning/scrolling; reduce browser gesture interference */
  touch-action: pan-x pan-y; /* keeps scroll + taps; avoids pinch gestures */
  overscroll-behavior: contain;
}

/* Disable iOS long-press callouts on links/images (prevents "Open in New Tab") */
* {
  -webkit-touch-callout: none;
}

/* ---------- Desktop / wide screens (>= 700px): show stylish 5px scrollbar ---------- */
@media (min-width: 700px) {
  /* WebKit scrollbar back on, 5px wide */
  ::-webkit-scrollbar {
    display: initial;
    width: 5px;
    height: 5px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.6));
    border-radius: 9999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0.75));
  }

  /* Firefox: thin styled scrollbar */
  html {
    scrollbar-width: thin;
    /* thumb color, then track color */
    scrollbar-color: rgba(0,0,0,0.55) transparent;
  }
}

/* You can still put your global styles here */
</style>
